import { useTranslation } from "react-i18next"
import { TFunction } from "i18next"
import React, { useContext } from "react"
import useElmish, { Effect, Effects, Dispatch } from "react-use-elmish"
import { attemptFunction } from "react-use-elmish/dist/effects"
import { MarketingPreferences } from "src/utilities/httpClients/gatewayClient"
import { NavigateTo, useNavigateTo } from "src/hooks/useNavigateTo"
import { getErrorMessage } from "src/utilities/httpClients/gatewayClientHelpers"
import { runFunction } from "src/utilities/elmish"
import { useSetErrorMessage } from "src/providers/errorProvider"
import { useConfig } from "src/providers/configProvider"

const { none } = Effects

export type Action =
  | { type: "marketing_preferences_updated" }
  | {
      type: "marketing_preferences_verified"
      marketingPreferences: MarketingPreferences
    }
  | { type: "incorrect_email"; err: unknown }
  | { type: "person_anonymized"; err: unknown }
  | { type: "person_deleted"; err: unknown }
  | { type: "unknown_error"; err?: unknown }
  | { type: "start_verification" }

type State =
  | { isVerified: false }
  | {
      isVerified: true
      marketingPreferences: MarketingPreferences
    }

export const update =
  (
    navigateTo: NavigateTo,
    t: TFunction<["common", "marketingPreferences"]>,
    setErrorMessage: (error?: string | undefined) => void,
    memberServicesContact: string,
  ) =>
  (state: State, action: Action): [State, Effect<Action>] => {
    switch (action.type) {
      case "marketing_preferences_updated":
        return [
          state,
          attemptFunction(
            () =>
              navigateTo({
                page: "marketingPreferences/complete",
              }),
            () => ({ type: "unknown_error" }),
          ),
        ]
      case "marketing_preferences_verified":
        return [
          {
            isVerified: true,
            marketingPreferences: action.marketingPreferences,
          },
          attemptFunction(
            () => {
              setErrorMessage()
              navigateTo({
                page: "marketingPreferences/update",
              })
            },
            () => ({ type: "unknown_error" }),
          ),
        ]
      case "incorrect_email":
        return [
          {
            isVerified: false,
          },
          runFunction(() => {
            setErrorMessage(getErrorMessage(t, action.err))
          }),
        ]
      case "start_verification":
        return [
          {
            isVerified: false,
          },
          runFunction(() => setErrorMessage()),
        ]
      case "person_anonymized":
      case "person_deleted":
        return [
          {
            isVerified: false,
          },
          runFunction(() => {
            setErrorMessage(
              getErrorMessage(t, action.err, {
                memberServicesContact,
              }),
            )
          }),
        ]
      case "unknown_error": {
        return [
          state,
          runFunction(() => {
            setErrorMessage(getErrorMessage(t, action.err))
          }),
        ]
      }
    }
  }

type MarketingPreferencesContextProp = {
  state: State
  dispatch: Dispatch<Action>
}

const MarketingPreferencesContext =
  React.createContext<MarketingPreferencesContextProp>(
    undefined as unknown as MarketingPreferencesContextProp,
  )

export const MarketingPreferencesProvider: React.FCWithChildren = ({
  children,
}) => {
  const navigateTo = useNavigateTo()

  const { t } = useTranslation(["common", "marketingPreferences"])

  const { setErrorMessage } = useSetErrorMessage()

  const { memberServicesContact } = useConfig()

  const [state, dispatch] = useElmish(
    update(navigateTo, t, setErrorMessage, memberServicesContact),
    () => [{ isVerified: false, error: undefined } as const, none()],
  )

  return (
    <MarketingPreferencesContext.Provider value={{ state, dispatch }}>
      {children}
    </MarketingPreferencesContext.Provider>
  )
}

export const useMarketingPreferences = () => {
  return useContext(MarketingPreferencesContext)
}
